import React, { useState, useEffect } from "react";
import SweetAlert from "react-bootstrap-sweetalert";
import { useSubscriptionApi } from "src/_common/hooks/actions/subscription/appSubscriptionApiHook";
import { useToaster } from "src/_common/hooks/actions/common/appToasterHook";
import { useTranslation } from "src/_common/hooks/useTranslation";
import BillingInfoModal from "src/components/modal/BillingInfoModal";
import PaymentModal from "src/components/modal/PaymentModal";

interface SubscriptionPlansGridProps {
  subscriptionPlanData: any;
  currentPlanData: any;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  billingPeriod: "monthly" | "yearly";
  onPlanUpgradeSuccess?: () => void;
  showCurrentPlan?: boolean;
  onLoadSubscriptionPlans?: () => void;
}

const SubscriptionPlansGrid: React.FC<SubscriptionPlansGridProps> = ({
  subscriptionPlanData,
  currentPlanData,
  isLoading,
  setIsLoading,
  billingPeriod,
  onPlanUpgradeSuccess,
  showCurrentPlan = true,
  onLoadSubscriptionPlans,
}) => {
  const subscriptionApi = useSubscriptionApi();
  const toast = useToaster();
  const { t } = useTranslation("payment");

  const [showBillingInfoModal, setShowBillingInfoModal] = useState(false);
  const [selectedPlanForUpgrade, setSelectedPlanForUpgrade] = useState<string | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<any>(null);
  const [showConfirmFreePlan, setShowConfirmFreePlan] = useState(false);
  const [freePlanData, setFreePlanData] = useState<any>(null);

  // Helper function to get translated feature label
  const getFeatureLabel = (key: string): string => {
    return t(`subscriptionPlans.features.${key}`);
  };

  // Helper function to render feature value based on type
  const renderFeatureValue = (key: string, value: any) => {
    if (typeof value === "boolean") {
      return <span className={value ? "feature-check" : "feature-cross"}>{value ? "✓" : "✗"}</span>;
    }

    // Handle numeric values
    if (typeof value === "string") {
      // If value is -1, show red cross icon
      if (value == "-1") {
        if (key === "interview_recording") {
          return (
            <span className="feature-text" style={{ fontWeight: "bold" }}>
              0 {t("subscriptionPlans.features.days")}
            </span>
          );
        }
        return <span className="feature-cross">✗</span>;
      }
    }
    if (typeof value === "number") {
      // If value is 0, show "Unlimited"
      if (value == -1) {
        if (key === "interview_recording") {
          return (
            <span className="feature-text" style={{ fontWeight: "bold" }}>
              0 {t("subscriptionPlans.features.days")}
            </span>
          );
        }
      }
      if (value === 0) {
        return (
          <span className="feature-text" style={{ color: "#28a745", fontWeight: "bold" }}>
            {t("subscriptionPlans.features.unlimited")}
          </span>
        );
      }

      // If value is 1 or more, display the number
      if (value >= 1) {
        const displayValue =
          key === "interview_recording"
            ? `${value.toLocaleString()} ${t("subscriptionPlans.features.days")}`
            : value.toLocaleString();
        return (
          <span className="feature-text" style={{ fontWeight: "bold" }}>
            {displayValue}
          </span>
        );
      }
    }

    return <span className="feature-text">{String(value)}</span>;
  };

  // Helper function to render plan features from API data
  const renderPlanFeatures = (plan: any) => {
    const featureOrder = [
      "active_jobs",
      "talent_scoutting",
      "live_video_interviews",
      "resume_parsing",
      "interview_recording",
      "ai_interview",
      "ai_candidate_searching",
      "meet_now",
      "candidate_search",
      "candidate_video_cv",
      "ai_screening",
      "interview_scheduling",
      "recruitment_analytics",
      "employer_branding",
      "ai_offer_letter",
      "support",
      "listing_duration",
    ];

    // Access features from nested features object or directly from plan (for backward compatibility)
    const features = plan.features || plan;

    return featureOrder
      .map((featureKey) => {
        const featureValue = features[featureKey];
        const featureLabel = getFeatureLabel(featureKey);

        // Skip if feature doesn't exist in the plan
        if (featureValue === undefined || featureValue === null) {
          return null;
        }

        return (
          <li key={featureKey}>
            {renderFeatureValue(featureKey, featureValue)}
            <span className="feature-label" style={{ marginLeft: "8px" }}>
              {featureLabel}
            </span>
          </li>
        );
      })
      .filter(Boolean); // Remove null entries
  };

  const handleBillingInfoSuccess = () => {
    console.log("Billing info saved successfully");
    // After billing info is saved, reload subscription plans to update is_billing_info flag
    if (onLoadSubscriptionPlans) {
      onLoadSubscriptionPlans();
    }

    // After billing info is saved, proceed with upgrade
    setShowBillingInfoModal(false);
    if (selectedPlanForUpgrade) {
      // Continue with the upgrade process for the stored plan
      setSelectedPlanForUpgrade(null); // Clear stored plan

      // Open payment modal instead of direct upgrade
      if (selectedPlan) {
        setShowPaymentModal(true);
      }
    }
  };

  const handlePaymentSuccess = () => {
    if (selectedPlan) {
      toast.success(`${t("subscriptionPlans.messages.upgradeSuccess")} ${selectedPlan.name}`);
      setShowPaymentModal(false);
      setSelectedPlan(null); // Clear stored plan after success

      if (onPlanUpgradeSuccess) {
        onPlanUpgradeSuccess();
      }
    }
  };

  const handlePaymentModalFromBilling = (planData: any) => {
    // Close billing info modal and open payment modal with plan data
    setShowBillingInfoModal(false);
    setShowPaymentModal(true);
  };

  const handleFreePlanConfirm = () => {
    if (!freePlanData) return;

    setShowConfirmFreePlan(false);
    setIsLoading(true);

    subscriptionApi.callCreatePayment(
      {
        plan_id: freePlanData.id,
        gateway_type: "P",
      },
      (message: string, response: any) => {
        toast.success(message || t("subscriptionPlans.messages.subscribeSuccess"));

        if (onLoadSubscriptionPlans) {
          onLoadSubscriptionPlans(); // Refresh plan data
        }

        setIsLoading(false);
        setSelectedPlan(null);
        setSelectedPlanForUpgrade(null);
        setFreePlanData(null);

        if (onPlanUpgradeSuccess) {
          onPlanUpgradeSuccess();
        }
      },
      (error: any) => {
        console.error("Failed to create payment for free plan:", error);
        toast.error(error?.message || t("subscriptionPlans.messages.subscribeFailed"));
        setIsLoading(false);
        setFreePlanData(null);
      },
    );
  };

  const handleFreePlanCancel = () => {
    setShowConfirmFreePlan(false);
    setSelectedPlan(null);
    setSelectedPlanForUpgrade(null);
    setFreePlanData(null);
  };

  const handlePlanUpgrade = (planId: string) => {
    // Store selected plan data
    const plan = subscriptionPlanData?.plans.find((p: any) => p.id === planId);
    if (plan) {
      const planPrice = plan.type === 1 ? plan.price : plan.price;
      const planInfo = {
        id: plan.id,
        name: plan.title,
        price: `${planPrice}`,
        aed_converted_price: `${plan.aed_converted_price}`,
        period: plan.type === 1 ? "/ month" : "/ year",
        description: plan.description || "",
        features: [],
      };
      setSelectedPlan(planInfo);
      setSelectedPlanForUpgrade(planId);

      // For free plans, show confirmation dialog
      if (planPrice === 0) {
        setFreePlanData(plan);
        setShowConfirmFreePlan(true);
        return;
      }

      // Check if billing info is required for paid plans
      if (!subscriptionPlanData?.is_billing_info) {
        setShowBillingInfoModal(true);
        return;
      } else {
        // If billing info exists, proceed directly to payment
        setShowPaymentModal(true);
      }
    }
  };

  const renderPlanCard = (plan: any) => {
    //console.log('Rendering plan:################', currentPlanData);
    // Match current plan by title and type since subscription_plan_id may not match plan.id
    // For Essential plans, show both monthly and yearly as current if user has Essential plan
    let isCurrentPlan = currentPlanData?.title === plan.title && currentPlanData?.type === plan.type;

    // If current plan is Essential, mark both monthly and yearly Essential plans as current
    if (currentPlanData?.title === "Essential" && plan.title === "Essential") {
      isCurrentPlan = true;
    }

    // Check if plan is expired based on period_end date
    const isPlanExpired = isCurrentPlan && currentPlanData?.period_end && new Date(currentPlanData.period_end) < new Date();

    const price = plan?.type === 1 ? plan.price : plan.price;
    const priceDisplay = price === 0 ? t("subscriptionPlans.pricing.free") : `$${price}`;
    const periodDisplay =
      plan?.type === 1 ? t("subscriptionPlans.pricing.perMonth") : t("subscriptionPlans.pricing.perYear");

    return (
      <div key={plan.id} className={`subscription-plan-card ${isCurrentPlan ? "current-plan" : ""} ${isPlanExpired ? "expired-plan" : ""}`}>
        {plan.title === "Enterprise" ? (
          <div className="plan-badge">{t("subscriptionPlans.badges.mostPopular")}</div>
        ) : null}
        {isCurrentPlan ? (
          <div className={`plan-current ${isPlanExpired ? "expired" : ""}`}>
            {isPlanExpired ? t("subscriptionPlans.badges.expired") : t("subscriptionPlans.buttons.currentPlan")}
          </div>
        ) : null}

        <div className="plan-header">
          <h3 className="plan-name">{plan.title}</h3>
          {plan?.type === 1 ? (
            <div className="plan-price">
              {plan?.strike_price != 0 && price > 0 && (
                <span
                  className="price-strikethrough"
                  style={{ textDecoration: "line-through", marginRight: "8px", opacity: 0.6 }}
                >
                  ${plan.strike_price}
                </span>
              )}
              <span className="price">{priceDisplay}</span>
              {price > 0 && <span className="period">{periodDisplay}</span>}
            </div>
          ) : (
            <div className="plan-price">
              {plan?.strike_price != 0 && price > 0 && (
                <span
                  className="price-strikethrough"
                  style={{ textDecoration: "line-through", marginRight: "8px", opacity: 0.6 }}
                >
                  ${plan.strike_price}
                </span>
              )}
              <span className="price">
                {price === 0
                  ? t("subscriptionPlans.pricing.free")
                  : `$${Math.round(((parseFloat(price) / 12) * 100) / 100)}`}
              </span>
              {price > 0 && <span className="period">{t("subscriptionPlans.pricing.perMonth")}</span>}
            </div>
          )}
          {plan?.type !== 1 && price > 0 ? (
            <div className="plan-price">
              {t("subscriptionPlans.billedYearlyAt")}
              <strong className="price-yearly ms-1">{priceDisplay}</strong>
              {price > 0 && <span className="period">{periodDisplay}</span>}
            </div>
          ) : (
            <div className="plan-price">
              <strong className="price-yearly ms-1"></strong>
            </div>
          )}
          <p className="plan-description">{plan.description}</p>
        </div>

        <div className="plan-features">
          <ul style={{ listStyle: "none", padding: 0 }}>{renderPlanFeatures(plan)}</ul>
        </div>

        <div className="plan-action">
          {isCurrentPlan ? (
            <button className="plan-button current-plan-button d-none" disabled>
              {t("subscriptionPlans.buttons.currentPlan")}
            </button>
          ) : price > 0 ? (
            <button
              className="plan-button upgrade-button"
              onClick={() => handlePlanUpgrade(plan.id)}
              disabled={isLoading}
            >
              {t("subscriptionPlans.buttons.choose")}
            </button>
          ) : null}
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="subscription-plans-grid">
        {subscriptionPlanData?.plans
          ?.filter((plan: any) => {
            // Match current plan by title and type since subscription_plan_id may not match plan.id
            const isPlanCurrentPlan = currentPlanData?.title === plan.title && currentPlanData?.type === plan.type;
            return showCurrentPlan || !isPlanCurrentPlan;
          })
          ?.filter((plan: any) => (billingPeriod === "monthly" ? plan.type === 1 : plan.type !== 1))
          ?.map((plan: any) => renderPlanCard(plan))}
      </div>

      {/* Billing Info Modal */}
      <BillingInfoModal
        isVisible={showBillingInfoModal}
        onClose={() => {
          setShowBillingInfoModal(false);
          setSelectedPlanForUpgrade(null);
          setSelectedPlan(null);
        }}
        onSuccess={handleBillingInfoSuccess}
        selectedPlan={selectedPlan}
        onPaymentModalOpen={handlePaymentModalFromBilling}
      />

      {/* Payment Modal */}
      {selectedPlan && (
        <PaymentModal
          isVisible={showPaymentModal}
          onClose={() => {
            // Refresh subscription plans when payment modal closes without success
            if (onLoadSubscriptionPlans) {
              onLoadSubscriptionPlans();
            }

            setShowPaymentModal(false);
            setSelectedPlan(null);
            setSelectedPlanForUpgrade(null);
          }}
          planName={selectedPlan.name}
          planPrice={selectedPlan.price}
          aed_converted_price={selectedPlan.aed_converted_price}
          planPeriod={selectedPlan.period}
          onPaymentSuccess={handlePaymentSuccess}
          planId={selectedPlan.id}
          billingPeriod={billingPeriod}
        />
      )}

      {/* Free Plan Confirmation */}
      {showConfirmFreePlan && freePlanData && (
        <SweetAlert
          warning
          showCancel
          confirmBtnText={
            isLoading
              ? t("subscriptionPlans.confirmFreePlan.processing")
              : t("subscriptionPlans.confirmFreePlan.confirm")
          }
          confirmBtnBsStyle="preview"
          cancelBtnText={t("subscriptionPlans.confirmFreePlan.cancel")}
          cancelBtnBsStyle="cancel"
          title={t("subscriptionPlans.confirmFreePlan.title")}
          onConfirm={handleFreePlanConfirm}
          onCancel={handleFreePlanCancel}
          disabled={isLoading}
        >
          {t("subscriptionPlans.confirmFreePlan.message")} <strong>{freePlanData.title}</strong>{" "}
          {t("subscriptionPlans.confirmFreePlan.plan")}
        </SweetAlert>
      )}
    </>
  );
};

export default SubscriptionPlansGrid;
